import type { Timestamp } from '@anytracker/core'
import { useState } from 'react'

import { Button } from '../../components/Button.js'
import { formatDateTime } from '../../format/dates.js'
import styles from './EntryForm.module.css'

interface Props {
  readonly at: Timestamp
  readonly now: Timestamp
  readonly onChange: (at: Timestamp) => void
}

/** The offsets that cover almost every real backdating. */
const QUICK = [15, 30, 60] as const

/**
 * When it happened. Now by default, adjustable without leaving the form.
 *
 * Most backdating is "I forgot for twenty minutes", so the quick offsets are
 * the primary control and the full picker is folded away. Reaching for a
 * date-time picker to record something from half an hour ago is the kind of
 * friction that stops logging happening at all.
 *
 * The **zone is preserved** across every adjustment. Backdating changes when,
 * not where — an entry corrected an hour later was still recorded in the zone
 * the user is standing in (ADR-0006).
 */
export function WhenControl({ at, now, onChange }: Props) {
  const [expanded, setExpanded] = useState(false)
  const backdated = Math.abs(now.epochMs - at.epochMs) > 60_000

  /** `<input type="datetime-local">` wants wall-clock text with no zone. */
  function toLocalInput(timestamp: Timestamp): string {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timestamp.timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(timestamp.epochMs)
    const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '00'
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
  }

  return (
    <div className={styles['when']}>
      <div className={styles['whenRow']}>
        <span
          className={`${styles['whenValue']} ${backdated ? styles['backdated'] : ''}`}
          data-testid="entry-time"
        >
          {backdated ? formatDateTime(at) : 'Now'}
        </span>
        <Button variant="ghost" onClick={() => setExpanded((open) => !open)}>
          {expanded ? 'Done' : 'Change time'}
        </Button>
      </div>

      {expanded && (
        <>
          <div className={styles['offsets']}>
            {QUICK.map((minutes) => (
              <Button
                key={minutes}
                onClick={() =>
                  onChange({
                    epochMs: now.epochMs - minutes * 60_000,
                    timeZone: at.timeZone,
                  })
                }
              >
                {minutes} min ago
              </Button>
            ))}
            {backdated && <Button onClick={() => onChange(now)}>Now</Button>}
          </div>

          <input
            type="datetime-local"
            className={styles['datetime']}
            aria-label="Date and time"
            value={toLocalInput(at)}
            onChange={(event) => {
              const parsed = Date.parse(event.target.value)
              if (Number.isFinite(parsed)) {
                /*
                 * `datetime-local` has no zone, so the browser parses it in the
                 * *device's* zone. That is correct for the normal case — the
                 * user is typing a local wall-clock time where they are — and
                 * the captured `timeZone` is preserved either way, which is
                 * what keeps the day boundary right (T-101).
                 */
                onChange({ epochMs: parsed, timeZone: at.timeZone })
              }
            }}
          />
        </>
      )}
    </div>
  )
}
